import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import type {Options} from '@contentful/rich-text-react-renderer'
import type {Document} from '@contentful/rich-text-types'
import {Box, FAQ, FAQGroup, Text} from '@primer/react-brand/esm'
import type {ComponentProps} from 'react'
import type {FaqGroup} from '../../types'
import {renderWithoutParagraphs, renderWithSpans} from '../../utils'

type FlexSuiteOutroFaqsProps = {
  groups: FaqGroup[]
  heading?: Document
  answerRenderOptions?: Options
  headingPaddingInlineStart?: ComponentProps<typeof Box>['paddingInlineStart']
  showSingleGroupHeadingWithHeading?: boolean
}

export function getCompleteFaqGroups(groups?: FaqGroup[]) {
  return groups?.filter(group => group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer)) ?? []
}

function renderFaqItems(group: FaqGroup, answerRenderOptions?: Options) {
  return group.fields.faqs
    .filter(faq => faq.fields.question && faq.fields.answer)
    .map((faq, index) => (
      <FAQ.Item key={faq.sys.id} name={`faq-${group.sys.id}`} open={index === 0}>
        <FAQ.Question>{faq.fields.question}</FAQ.Question>
        <FAQ.Answer>
          <Text as="div" variant="muted">
            {documentToReactComponents(faq.fields.answer, answerRenderOptions)}
          </Text>
        </FAQ.Answer>
      </FAQ.Item>
    ))
}

function FaqHeading({
  heading,
  paddingInlineStart,
}: {
  heading: Document
  paddingInlineStart: ComponentProps<typeof Box>['paddingInlineStart']
}) {
  return (
    <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart={paddingInlineStart}>
      {renderWithSpans(heading, 'd-block')}
    </Box>
  )
}

export function FlexSuiteOutroFaqs({
  groups,
  heading,
  answerRenderOptions,
  headingPaddingInlineStart = 'spacious',
  showSingleGroupHeadingWithHeading = false,
}: FlexSuiteOutroFaqsProps) {
  if (groups.length === 0) return null

  const singleGroup = groups.length === 1 ? groups[0] : undefined

  if (singleGroup) {
    return (
      <FAQ key={singleGroup.sys.id} variant="gridline">
        {heading ? (
          <FAQ.Heading>
            <FaqHeading heading={heading} paddingInlineStart={headingPaddingInlineStart} />
          </FAQ.Heading>
        ) : null}
        {singleGroup.fields.heading && (!heading || showSingleGroupHeadingWithHeading) ? (
          <FAQ.Heading>{renderWithoutParagraphs(singleGroup.fields.heading)}</FAQ.Heading>
        ) : null}
        {renderFaqItems(singleGroup, answerRenderOptions)}
      </FAQ>
    )
  }

  return (
    <FAQGroup variant="gridline" defaultSelectedIndex={0}>
      {heading ? (
        <FAQGroup.Heading>
          <FaqHeading heading={heading} paddingInlineStart={headingPaddingInlineStart} />
        </FAQGroup.Heading>
      ) : null}
      {groups.map(group => (
        <FAQ key={group.sys.id} variant="gridline">
          {group.fields.heading ? <FAQ.Heading>{renderWithoutParagraphs(group.fields.heading)}</FAQ.Heading> : null}
          {renderFaqItems(group, answerRenderOptions)}
        </FAQ>
      ))}
    </FAQGroup>
  )
}
